import React, {useRef, useMemo, memo} from 'react';
import {Props} from './ac-initials.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {SIZES, THEMES} from '@asdf-constants';

// Imports => Utilities
import {
  AcIsSet,
  AcSanitize,
  AcGenerateThemeClassNames,
  AcFormatInitials,
  AcCapitalize,
} from '@asdf-utils';

// Imports => Atoms
import {AcRipple} from '@asdf-atoms';

const _CLASSES = {
  MAIN: 'ac-initials',
  DISABLED: 'ac-initials--disabled',
  AVATAR: 'ac-initials__avatar',
  THEME: AcGenerateThemeClassNames('ac-initials'),
};

const AcInitials = memo(
  ({
    firstname = null,
    lastname = null,
    theme = 'default',
    disabled = false,
    callback,
    className,
    ...rest
  }) => {
    const $element = useRef();

    const handleClick = event => {
      if (!disabled && callback) callback(event, $element.current);
    };

    const getAvatarClassNames = useMemo(() => {
      return clsx(_CLASSES.AVATAR.MAIN);
    }, []);

    const getMainClassNames = useMemo(() => {
      return clsx(
        _CLASSES.MAIN,
        theme && _CLASSES.THEME[theme.toUpperCase()],
        disabled && _CLASSES.DISABLED,
        className
      );
    }, [theme, disabled, className]);

    const getInitials = useMemo(() => {
      if (!AcIsSet(firstname)) return null;

      let name = `${firstname}`;
      if (AcIsSet(lastname)) name = `${name} ${lastname}`;

      return AcFormatInitials(name);
    }, [firstname, lastname]);

    const renderInitials = useMemo(() => {
      if (!AcIsSet(getInitials)) return null;

      return <div className={getAvatarClassNames}>{getInitials}</div>;
    }, [getInitials]);

    const renderRipple = useMemo(() => {
      let ripple_theme;

      if ([THEMES.WHITE, THEMES.LIGHT, THEMES.SUBTLE].indexOf(theme) > -1)
        ripple_theme = THEMES.PITCH;
      else if ([THEMES.OMEGA, THEMES.OMEGA_LIGHT].indexOf(theme) > -1) ripple_theme = THEMES.OMEGA;
      else if ([THEMES.ALPHA, THEMES.ALPHA_LIGHT].indexOf(theme) > -1) ripple_theme = THEMES.ALPHA;
      else if (
        [THEMES.ALPHA, THEMES.ALPHA_LIGHT, THEMES.OMEGA, THEMES.OMEGA_LIGHT].indexOf(theme) > -1
      )
        ripple_theme = THEMES.PITCH;

      return <AcRipple theme={ripple_theme} size={'small'} simple />;
    }, [theme]);

    return (
      <div
        ref={$element}
        aria-roledescription={'button'}
        disabled={disabled}
        onClick={handleClick}
        className={getMainClassNames}
        {...rest}
      >
        {renderRipple}
        {renderInitials}
      </div>
    );
  }
);

AcInitials.displayName = 'AcInitials';
AcInitials.propTypes = Props;

export {AcInitials};
export default AcInitials;
